import { Link } from "react-router-dom";
import { SeoTags } from "../../components/Head/SeoTags";
import { RelatedGuide } from "../../components/RelatedGuide/RelatedGuide";
import { useTranslation } from "react-i18next";

const UseCasePage = ({ useCase }) => {
  const { t } = useTranslation();
  const base = `useCases.${useCase}`;

  // Use case specific icons and colors
  const useCaseData = {
    developers: {
      icon: "bi-code-slash",
      color: "primary",
      features: ["API Documentation", "GitHub repos", "Stack Overflow", "Tech tutorials", "Code snippets"]
    },
    researchers: {
      icon: "bi-journal-text",
      color: "success",
      features: ["Academic papers", "Research databases", "Citations", "Methodology guides", "Data sources"]
    },
    students: {
      icon: "bi-mortarboard",
      color: "info",
      features: ["Course materials", "Study guides", "Reference sources", "Assignment resources", "Tutorial videos"],
      relatedGuide: {
        to: "/blog/organize-bookmarks-for-students",
        title: "How to Organize Bookmarks for Students — 2026 Guide",
        description: "A setup that survives a full semester of sources and deadlines."
      }
    },
    designers: {
      icon: "bi-palette",
      color: "warning",
      features: ["Design inspiration", "Color palettes", "Typography resources", "UI kits", "Portfolio examples"]
    },
    marketers: {
      icon: "bi-megaphone",
      color: "danger",
      features: ["Campaign resources", "Analytics tools", "Competitor research", "Content ideas", "Industry news"]
    }
  };

  const data = useCaseData[useCase] || useCaseData.developers;

  return (
    <>
      <SeoTags
        title={t(`${base}.seo.title`)}
        description={t(`${base}.seo.description`)}
        keywords={t(`${base}.seo.keywords`)}
      />

      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: t(`${base}.seo.title`),
            description: t(`${base}.seo.description`),
            url: `https://onbookmarks.com/bookmarks-for-${useCase}`,
            mainEntity: {
              "@type": "SoftwareApplication",
              name: "onBookmarks",
              applicationCategory: "ProductivityApplication",
              operatingSystem: "Web, iOS, Android",
              offers: {
                "@type": "Offer",
                price: "0",
                priceCurrency: "USD",
              },
            },
          }),
        }}
      />

      {/* Hero Section */}
      <section className="hero bg-light">
        <div className="container">
          <div className="row align-items-center min-vh-50">
            <div className="col-lg-8 mx-auto text-center">
              <div>
                <div className={`d-inline-block rounded-4 bg-${data.color}-subtle fs-normal text-${data.color} px-3 py-2 mb-4`}>
                  <i className={`bi ${data.icon} me-2`}></i>
                  {t(`${base}.hero.badge`)}
                </div>
              </div>
              <h1 className="display-4 fw-bold mb-4">
                {t(`${base}.hero.title`)}
              </h1>
              <p className="lead mb-4">
                {t(`${base}.hero.description`)}
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-primary btn-lg px-5"
                >
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  {t(`${base}.hero.cta`)}
                </Link>
                <Link to="/features" className="btn btn-outline-primary btn-lg px-4">
                  {t(`${base}.hero.ctaSecondary`)}
                </Link>
              </div>
              <p className="mt-3 text-muted">
                <i className="bi bi-shield-check me-2"></i>
                {t(`${base}.hero.guarantee`)}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">
              {t(`${base}.problem.title`)}
            </h2>
            <p className="lead text-muted">
              {t(`${base}.problem.description`)}
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-danger-subtle rounded-3 mb-3">
                    <i className="bi bi-folder2-open fs-3 text-danger"></i>
                  </div>
                  <h3 className="h5 mb-3">{t(`${base}.problem.disorganization.title`)}</h3>
                  <p className="text-muted">{t(`${base}.problem.disorganization.description`)}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3">
                    <i className="bi bi-search fs-3 text-warning"></i>
                  </div>
                  <h3 className="h5 mb-3">{t(`${base}.problem.findability.title`)}</h3>
                  <p className="text-muted">{t(`${base}.problem.findability.description`)}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-secondary-subtle rounded-3 mb-3">
                    <i className="bi bi-clock fs-3 text-secondary"></i>
                  </div>
                  <h3 className="h5 mb-3">{t(`${base}.problem.timeWaste.title`)}</h3>
                  <p className="text-muted">{t(`${base}.problem.timeWaste.description`)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-6 fw-bold mb-4">
              {t(`${base}.solution.title`)}
            </h2>
            <p className="lead text-muted">
              {t(`${base}.solution.description`)}
            </p>
          </div>

          <div className="row g-4">
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-primary-subtle rounded-3 mb-3">
                    <i className="bi bi-robot fs-3 text-primary"></i>
                  </div>
                  <h3 className="h5 mb-3">{t(`${base}.solution.ai.title`)}</h3>
                  <p className="text-muted">{t(`${base}.solution.ai.description`)}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-success-subtle rounded-3 mb-3">
                    <i className="bi bi-collection fs-3 text-success"></i>
                  </div>
                  <h3 className="h5 mb-3">{t(`${base}.solution.organization.title`)}</h3>
                  <p className="text-muted">{t(`${base}.solution.organization.description`)}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card h-100 border-0 shadow-sm">
                <div className="card-body p-4">
                  <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3">
                    <i className="bi bi-lightning fs-3 text-warning"></i>
                  </div>
                  <h3 className="h5 mb-3">{t(`${base}.solution.search.title`)}</h3>
                  <p className="text-muted">{t(`${base}.solution.search.description`)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Bookmarks Section */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h2 fw-bold mb-4">{t(`${base}.bookmarks.title`)}</h2>
            <p className="lead text-muted">{t(`${base}.bookmarks.description`)}</p>
          </div>

          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="d-flex flex-wrap justify-content-center gap-3">
                {data.features.map((feature, index) => (
                  <span key={index} className={`badge bg-${data.color}-subtle text-${data.color} px-3 py-2 fs-6`}>
                    <i className="bi bi-bookmark-check me-2"></i>
                    {feature}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="h2 fw-bold mb-4">{t(`${base}.features.title`)}</h2>
          </div>

          <div className="row g-4">
            <div className="col-md-6 col-lg-3">
              <div className="text-center">
                <div className="icon icon-lg bg-primary-subtle rounded-3 mb-3 mx-auto d-flex align-items-center justify-content-center" style={{width: '64px', height: '64px'}}>
                  <i className="bi bi-robot fs-3 text-primary"></i>
                </div>
                <h4 className="h6 mb-2">AI Auto-Tagging</h4>
                <p className="text-muted small">Automatically categorize and tag your bookmarks</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3">
              <div className="text-center">
                <div className="icon icon-lg bg-success-subtle rounded-3 mb-3 mx-auto d-flex align-items-center justify-content-center" style={{width: '64px', height: '64px'}}>
                  <i className="bi bi-collection fs-3 text-success"></i>
                </div>
                <h4 className="h6 mb-2">Multi-Collection</h4>
                <p className="text-muted small">One bookmark in multiple collections</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3">
              <div className="text-center">
                <div className="icon icon-lg bg-warning-subtle rounded-3 mb-3 mx-auto d-flex align-items-center justify-content-center" style={{width: '64px', height: '64px'}}>
                  <i className="bi bi-shield-lock fs-3 text-warning"></i>
                </div>
                <h4 className="h6 mb-2">Encrypted Privacy</h4>
                <p className="text-muted small">Client-side encryption for sensitive links</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-3">
              <div className="text-center">
                <div className="icon icon-lg bg-info-subtle rounded-3 mb-3 mx-auto d-flex align-items-center justify-content-center" style={{width: '64px', height: '64px'}}>
                  <i className="bi bi-search fs-3 text-info"></i>
                </div>
                <h4 className="h6 mb-2">Instant Search</h4>
                <p className="text-muted small">Find any bookmark in milliseconds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {data.relatedGuide && (
        <RelatedGuide
          to={data.relatedGuide.to}
          title={data.relatedGuide.title}
          description={data.relatedGuide.description}
        />
      )}

      {/* CTA Section */}
      <section className="py-5 banner-gradient text-white">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <h4 className="h1 fw-bold mb-4 text-white">
                {t(`${base}.cta.title`)}
              </h4>
              <p className="lead mb-4">
                {t(`${base}.cta.description`)}
              </p>
              <div className="d-flex gap-3 justify-content-center flex-wrap">
                <Link
                  to={`${process.env.REACT_APP_APP_URL}/register`}
                  className="btn btn-light btn-lg px-5"
                >
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  {t(`${base}.cta.button`)}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default UseCasePage;
